import './StripCalendarEvent.scss';
import { useEffect, useState } from 'react';
import StripCalendarEventCapsule from './StripCalendarEventCapsule/StripCalendarEventCapsule';
import StripCalendarEventDetails from './StripCalendarEventDetails/StripCalendarEventDetails';
import { getReleaseMeasurements } from '../CalendarReleaseCapsule/CalendarReleaseCapsuleHelper';
import { eventActivityType, RELEASE_TYPE, STRIP_CAL_EVENT_LEVEL_REM } from '../../constants/myBlockMonth';
import { convertToDateObject, convertToDateTime } from '../../helpers/dateHelpers';
import { formatNumToTime } from '../../helpers/timeHelpers';
import { StripCalendarMappedEvent } from '../../models/MyBlockMonthInterface';
import helper from '../../pages/MyBlockMonth/intersectingElementsHelper';
import { checkEventCarryOut, getClassNameForEarlyOrLate, getEventName, getStripCalendarCapsuleWidth, getTrainingRouteDetails, SPECIAL_EVENT_TYPES } from '../../pages/MyBlockMonth/myBlockMonthHelper';
import { useProfile } from '../../providers/Profile';

const StripCalendarEvent = ({actualView, event, blockDate, calendarElementWidth, dates, handleEventClick, key}) => {
    const { checkMinWidth } = helper(document);
    const profileContext = useProfile();
    const [mappedEvent, setMappedEvent] = useState<StripCalendarMappedEvent>({} as any);
    const carryIn = convertToDateObject(event.start_date, '00:00') < blockDate.start;
    const carryOut = checkEventCarryOut(blockDate.end, event)

    const getEventLayoverDetails = (event) => {
        if (event.activityType === eventActivityType.PAIRING) {
            return event.layover;
        }
        else if (event.activityType === eventActivityType.TRAINING) {
            return getTrainingRouteDetails(event.activityId, profileContext);
        }
    }

    /**
     * Align the event on the x axis, to determine where it starts
     * @param event 
     * @param carryIn
     * @param singleDay 
     * @returns the margin left of the event
     */
     const getEventMarginLeft = (event, carryIn) => { 

        /* carry-in event starts at the beginning of the calendar */
        if(carryIn) return 0;

        let startDate = convertToDateObject(event.start_date, '00:00');
        if(event.hasStartRelease) startDate = convertToDateObject(event.release[0].start_date, '00:00');
        const startDayNumber = startDate.getDate();
        const startMonth = startDate.getMonth()+1;

        /* the index of the date at which the event starts */
        const startDateIndex = dates.findIndex(date => date.number === startDayNumber && date.month === startMonth);

        let startDateAndCheckIn = convertToDateObject(event.start_date, formatNumToTime(event.checkIn.value));
        if(event.hasStartRelease) startDateAndCheckIn = convertToDateObject(event.release[0].start_date, formatNumToTime(event.release[0].checkIn.value));

        /* the check-in time percentage relative to 24hrs */
        const checkInTimePercentage = Math.round(100 - ((24 - startDateAndCheckIn.getHours()-(startDateAndCheckIn.getMinutes()/60))/ 24) * 100);
        
        /* multiply the index of the startDate by the width of the calendar element to get the initial margin left, then add the check-in padding */
        return (startDateIndex * calendarElementWidth) + ((checkInTimePercentage/100) * calendarElementWidth);
    }

    /**
     * @param event
     * @returns the event's margin top based on activity type
     */
    const getEventMarginTop = (event) => {
        switch(event.stripHeightLevel) {
            case 1:
                return STRIP_CAL_EVENT_LEVEL_REM.ONE;
            case 2: 
                return STRIP_CAL_EVENT_LEVEL_REM.TWO;
            case 3: 
                return STRIP_CAL_EVENT_LEVEL_REM.THREE;
            default:
                return STRIP_CAL_EVENT_LEVEL_REM.ONE;
        }
    }

    const mapEventProperties = () => {
        setMappedEvent({
            ...event,
            activityId: event.activityId,
            activityType: event.activityType,
            carryIn: carryIn,
            carryOut: carryOut,
            checkInTime: formatNumToTime(event.checkIn.value),
            checkInTimeDeviation: event.checkIn.deviation,
            checkOutTime: formatNumToTime(event.checkOut.value),
            checkOutTimeDeviation: event.checkOut.deviation,
            endDate: event.end_date,
            layover: getEventLayoverDetails(event),
            marginLeft: getEventMarginLeft(event, carryIn),
            marginTop: getEventMarginTop(event),
            releasePaddingLeft: event.hasStartRelease ? getReleaseMeasurements(event, RELEASE_TYPE.START_MARGIN_LEFT, 0) : 0,
            remarks: event.remarks,
            specialEvent: SPECIAL_EVENT_TYPES.includes(event.activityType),
            startDate: event.start_date,
            unique_id: event.unique_id,
            width: getStripCalendarCapsuleWidth(carryIn, carryOut, event, blockDate, calendarElementWidth)
        });
    }

    useEffect(() => {
         mapEventProperties();
         setTimeout(() => {
            checkMinWidth('event__id');
            checkMinWidth('event__check-in-time');
            checkMinWidth('event__check-out-time');
         }, 1)

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event, calendarElementWidth])

    const getCheckInTimeElement = () => {
        const className = getClassNameForEarlyOrLate(mappedEvent.checkInTimeDeviation, actualView, 'event__check-in-time');
        let visibility: any =  mappedEvent.carryIn ? 'hidden' : 'visible';

        if(mappedEvent.carryIn) {
            visibility = 'hidden';
            let dayBeforeBlockDateStart = new Date(+blockDate.start);
            dayBeforeBlockDateStart.setDate(dayBeforeBlockDateStart.getDate()-1);
            if(convertToDateTime(mappedEvent.startDate, '00:00') === dayBeforeBlockDateStart.getTime()) {
               visibility = 'visible';
            }
        }
        const time = event.hasStartRelease ? formatNumToTime(event.release[0].checkOut.value) : mappedEvent.checkInTime;
        return (
            <p 
                id={`ci_${mappedEvent.unique_id}`} 
                className={className} 
                style={{visibility: visibility, marginLeft: mappedEvent.releasePaddingLeft+'%'}}
            >
                {time}
            </p>
        )
    }

    const getCheckOutTimeElement = () => {
        let className = getClassNameForEarlyOrLate(mappedEvent.checkOutTimeDeviation, actualView, 'event__check-out-time');
        let float = mappedEvent.width < 2 || event.hasStartRelease || event.hasEndRelease? 'left' : 'right' as any; 
        let time = event.hasEndRelease ? formatNumToTime(event.release[event.release.length-1].checkIn.value) : mappedEvent.checkOutTime; 
        let visibility = 'visible' as any;

        if(mappedEvent.activityType === eventActivityType.GDO || mappedEvent.activityType === eventActivityType.GDO6 || mappedEvent.activityType === eventActivityType.TDO || mappedEvent.activityType === eventActivityType.TDO6) {
            className = getClassNameForEarlyOrLate(mappedEvent.checkInTimeDeviation, actualView, 'event__check-out-time');
            float = 'left';
            time = mappedEvent.checkInTime;
        } 
        else if(SPECIAL_EVENT_TYPES.includes(mappedEvent.activityType)) {
            visibility = 'hidden';
        }
        else if(mappedEvent.carryOut) {
            visibility = 'hidden';
            let dayAfterBlockDateEnd = new Date(+blockDate.end);
            dayAfterBlockDateEnd.setDate(dayAfterBlockDateEnd.getDate()+1);
            if(convertToDateTime(mappedEvent.endDate, '23:59') === dayAfterBlockDateEnd.getTime()) {
                visibility = 'visible';
            }
        }
        return (
            <p 
                id={`co_${mappedEvent.unique_id}`} 
                className={className} 
                style={{float: float, marginLeft: mappedEvent.releasePaddingLeft+'%', visibility: visibility}}
            >
                {time}
            </p>
        )
    }

    return (
        Object.keys(mappedEvent).length !== 0 ? 
            <div className="c-strip-calendar-event" key={key}>
                <div 
                    id={`event__${mappedEvent.unique_id}`} 
                    className={event.release ? 'c-event event--release' : 'c-event'} 
                    onClick={() => handleEventClick(mappedEvent.unique_id)}
                    style={{
                        marginLeft: `${mappedEvent.marginLeft}%`,
                        marginTop: `${mappedEvent.marginTop}rem`, 
                        width: `${mappedEvent.width}%`
                    }}>

                    <StripCalendarEventDetails 
                        blockDate={blockDate}
                        className="event__id"
                        event={mappedEvent} 
                        value={getEventName(mappedEvent)}
                    />
                    
                    {!mappedEvent.specialEvent && 
                        <StripCalendarEventDetails 
                            blockDate={blockDate}
                            className="event__layover" 
                            event={mappedEvent} 
                            value={mappedEvent.layover}
                        />
                    }

                    {!mappedEvent.specialEvent && getCheckInTimeElement()}

                    <StripCalendarEventCapsule 
                        blockDate={blockDate} 
                        calendarElementWidth={calendarElementWidth} 
                        event={mappedEvent}
                    />

                    {mappedEvent.activityType !== eventActivityType.RESERVE && getCheckOutTimeElement()}
                </div>
            </div>
        : null
    )
}

export default StripCalendarEvent;